<script setup>
import { reactive, onMounted, computed, ref, watch, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { registerEvents } from "@/composables/components";
import { useNumberFormat } from "@/composables/useNumberFormat";
import * as DATA$farm from "@/apis/data/farm";

import RangeChart from "@/components/charts/rangechart/RangeChart.vue";

const { t, locale } = useI18n();

const compName = "MiniRange";
registerEvents(compName);
DATA$farm.register(compName);

const intlLocale = locale.value == "fr" ? "fr-CA" : "en-us";

const { dec2, comma } = useNumberFormat(intlLocale);

const TYPE_TITLES = {
    "52week": t("Week52Range"),
    day: t("DayRange"),
    volume: t("VolumeRange"),
};

const props = defineProps({
    /**
     * Stock symbol to display data for
     * @example "MSFT"
     */
    symbol: {
        type: String,
        default: "MSFT",
    },
    /**
     * Type of range to display. Multiple types can be specified using commas.
     * @values 52week, day, volume
     */
    type: {
        type: [Array, String],
        default: () => ["52week"],
    },
    /**
     * Volume range to display
     * @values 30day, 52week
     */
    volumeRange: {
        type: String,
        default: "30day",
    },
    /**
     * Primary color for the chart
     */
    primaryColor: {
        type: String,
        default: "#0477D1",
    },
    /**
     * Secondary color for the chart
     */
    secondaryColor: {
        type: String,
        default: "#6D737A",
    },
    /**
     * Color for empty bars in the chart
     */
    emptybarColor: {
        type: String,
        default: "#F6F6F6",
    },
    /**
     * Color for the current value in the chart
     */
    currentColor: {
        type: String,
        default: "#333333",
    },
    /**
     * Border color for the chart
     */
    borderColor: {
        type: String,
        default: "#E3E3E3",
    },
});

const state = reactive({
    isLoading: true,
});

const data = reactive({
    quotes: {},
});

const typeValues = reactive({});

const symbol = ref(props.symbol.toUpperCase());
const types = ref(
    props.type instanceof Array ? props.type : props.type.split(",").map(type => type.trim())
);
const toolContainer = ref(null);
const chartGap = ref(0);

const descriptions = computed(() => {
    return types.value.map(type => generateDescription(type));
});

const chartColors = computed(() => ({
    primary: props.primaryColor,
    secondary: props.secondaryColor,
    emptybar: props.emptybarColor,
    current: props.currentColor,
    border: props.borderColor,
}));

watch(
    () => props.symbol,
    async newSymbol => {
        symbol.value = newSymbol.toUpperCase();
        await fetchQuoteData();
        await updateTypeValues();
    }
);

watch(
    () => props.type,
    async newType => {
        types.value = typeof newType === "string" ? newType.split(",")?.map(type => type.trim()) : newType;
        await updateTypeValues();
    }
);

watch(
    () => props.volumeRange,
    async () => {
        await updateTypeValues();
    }
);

async function fetchQuoteData() {
    try {
        data.quotes = (await DATA$farm.get$enhanced$quotes(symbol.value))[symbol.value];
    } catch (error) {
        console.error(error);
    }
}

function calculateCompare(current, high, low, avg) {
    if (current === high) {
        return { compareValue: 0, compareType: "high" };
    } else if (current === low) {
        return { compareValue: 0, compareType: "low" };
    }

    if (avg) {
        const compareToAvg = Math.abs(Number(((current - avg) / avg) * 100));
        return { compareValue: compareToAvg, compareType: current > avg ? "low" : "high" };
    }

    const compareToLow = Math.abs(Number(((current - low) / low) * 100));
    const compareToHigh = Math.abs(Number(((current - high) / high) * 100));
    const closestValue = Math.abs(current - low) < Math.abs(current - high) ? low : high;
    const compareType = closestValue === low ? "low" : "high";
    const compareValue = Math.abs(Number(((current - closestValue) / closestValue) * 100));

    return {
        compareValue,
        compareType,
    };
}

function generateDescription(type) {
    const { compareValue, compareType } = calculateCompare(
        typeValues[type]?.current,
        typeValues[type]?.high,
        typeValues[type]?.low,
        typeValues[type]?.avg
    );

    const formattedCompareValue = compareValue.toFixed(0);
    const compareTypeText = compareType === "low" ? t("above") : t("below");

    const typeTextMap = {
        "52week": {
            high: t("week52High"),
            low: t("week52Low"),
            default: t("Week52"),
        },
        day: {
            high: t("dailyHigh"),
            low: t("dailyLow"),
            default: t("todays"),
        },
        volume: {
            high: t("averageVolume"),
            low: t("averageVolume"),
            default: t("averageVolume"),
        },
    };

    const volumeTextMap = {
        "30day": t("Day30AverageVolume"),
        "52week": t("Week52AverageVolume"),
    };

    if (compareValue === 0) {
        const compareTypeText =
            type === "52week"
                ? t("CurrentlyAtA52Week")
                : type === "day"
                  ? t("Currentlyattodays")
                  : t("CurrentlyAtTheAverageVolume");
        return `${type === "volume" ? compareTypeText : compareTypeText + " " + compareType}`;
    }

    if (compareValue < 1.0) {
        const typeText = typeTextMap[type][compareType] || typeTextMap[type].default;
        return `Slightly ${compareTypeText} ${typeText}`;
    }

    const typeText = typeTextMap[type].default;
    const volumeText = volumeTextMap[props.volumeRange];

    if (type === "volume" && typeValues[type]?.avg) {
        if (compareValue === 0) {
            return `${t("CurrentlyAtTheAverageVolume")}`;
        }
        return `${formattedCompareValue}% ${compareTypeText} ${volumeText}`;
    }

    return `${formattedCompareValue}% ${compareTypeText} ${typeText} ${t(compareType)}`;
}

async function updateTypeValues() {
    types.value.forEach(type => {
        const high = getHighValue(type).value;
        const low = getLowValue(type).value;
        const current = getCurrentValue(type).value;
        const avg = getAvgValue(type).value;

        typeValues[type] = { high, low, current, avg };
    });
}

function getHighValue(type) {
    return computed(() => {
        if (type === "day") {
            return data.quotes?.pricedata?.high;
        } else if (type === "52week") {
            const week52High = data.quotes?.fundamental?.week52high?.content;
            const dayHigh = data.quotes?.pricedata?.high ?? 0;
            return dayHigh > week52High ? dayHigh : week52High;
        } else if (type === "volume" && props.volumeRange === "52week") {
            return data.quotes?.fundamental?.week52highvol?.content;
        }
        return null;
    });
}

function getLowValue(type) {
    return computed(() => {
        if (type === "day") {
            return data.quotes?.pricedata?.low;
        } else if (type === "52week") {
            const week52Low = data.quotes?.fundamental?.week52low?.content;
            const dayLow = data.quotes?.pricedata?.low ?? 0;
            return dayLow < week52Low ? dayLow : week52Low;
        } else if (type === "volume" && props.volumeRange === "52week") {
            return data.quotes?.fundamental?.week52lowvol?.content;
        }
        return null;
    });
}

function getCurrentValue(type) {
    return computed(() => {
        if (type === "52week" || type === "day") {
            return data.quotes?.pricedata?.last;
        } else if (type === "volume") {
            return data.quotes?.pricedata?.sharevolume;
        }
        return null;
    });
}

function getAvgValue(type) {
    return computed(() => {
        if (
            type === "volume" &&
            (props.volumeRange === "30day" || !getHighValue(type).value || !getLowValue(type).value)
        ) {
            return data.quotes?.fundamental?.avgvol30days;
        }
        return null;
    });
}

watch(
    () => props.symbol,
    async () => {
        state.isLoading = true;
        await fetchQuoteData();
        await updateTypeValues();
        state.isLoading = false;
    }
);

onMounted(async () => {
    await fetchQuoteData();
    await updateTypeValues();
    state.isLoading = false;
    chartGap.value = getComputedStyle(toolContainer.value).getPropertyValue("--qmodii-chart-gap");
});
</script>

<template lang="pug">
.qmodii-ui-tool.qmodii-minirange(
    :class="{ 'qmodii-loading': state.isLoading }",
    ref="toolContainer"
)
    .qmodii-minirange-content(v-if="!state.isLoading")
        .qmodii-minirange-charts-cont
            .qmodii-minirange-charts-wrap(
                v-for="(chartType, index) in types",
                :style="{ width: `${100 / types.length - 1}%` }"
            )
                RangeChart(
                    :type="chartType",
                    :high="typeValues[chartType]?.high",
                    :low="typeValues[chartType]?.low",
                    :current="typeValues[chartType]?.current",
                    :avg="typeValues[chartType]?.avg",
                    :chartColors="chartColors",
                    :description="descriptions[index]",
                    :title="TYPE_TITLES[chartType]"
                )
                .qmodii-vsep(v-if="index < types.length - 1")
</template>

<style lang="stylus">
:root .qmodii-main
    --qmodii-minirange-chart-gap var(--qmodii-spacing-xl)
    --qmodii-minirange-border-gap var(--qmodii-spacing-md)

.qmodii-ui-tool.qmodii-minirange
    height 100%
    container-type inline-size
    container-name minirange

    .qmodii-minirange-content
        display flex
        flex-direction column
        align-items start
        height 100%
        gap var(--qmodii-spacing-sm)

        .qmodii-minirange-charts-cont
            width 100%
            display flex
            gap var(--qmodii-minirange-chart-gap)

            .qmodii-minirange-charts-wrap
                display flex

        .qmodii-vsep
            height 100%
            position relative
            left var(--qmodii-minirange-border-gap)
            border var(--qmodii-primary-border)
</style>

<style>
@container minirange (max-width: 768px) {
    .qmodii-minirange-charts-cont {
        flex-direction: column;

        .qmodii-minirange-charts-wrap {
            width: 100% !important;
        }
    }

    .qmodii-vsep {
        display: none;
    }
}
</style>
